// Main Flow Builder Application

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Snackbar,
  Alert,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as RunIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  MoreVert as MoreIcon,
  Download as ExportIcon,
  Upload as ImportIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import ChatWorkflowDesigner from './components/ChatWorkflowDesigner.tsx';
import { WorkflowEngine } from './engine/WorkflowEngine.ts';
import { ConnectorManager } from './engine/ConnectorManager.ts';
import { WorkflowDefinition, WorkflowExecution } from './types/workflow.ts';

interface FlowBuilderAppProps {
  projectId?: string;
  userId?: string;
  onBackToFoundation?: () => void;
}

const FlowBuilderApp: React.FC<FlowBuilderAppProps> = ({
  projectId,
  userId = 'current_user',
  onBackToFoundation
}) => {
  // Core instances
  const [connectorManager] = useState(() => new ConnectorManager());
  const [workflowEngine] = useState(() => new WorkflowEngine(connectorManager));

  // State
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
  const [currentWorkflow, setCurrentWorkflow] = useState<WorkflowDefinition | null>(null);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showWorkflowList, setShowWorkflowList] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Dialogs
  const [newWorkflowDialogOpen, setNewWorkflowDialogOpen] = useState(false);
  const [executionHistoryOpen, setExecutionHistoryOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Initialize with default new workflow
  useEffect(() => {
    const defaultWorkflow: WorkflowDefinition = {
      id: 'new_workflow',
      name: 'New Workflow',
      description: 'Start building your workflow with AI assistance',
      version: '1.0.0',
      nodes: [],
      edges: [],
      triggers: [],
      variables: [],
      settings: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: userId,
      status: 'draft'
    };

    setCurrentWorkflow(defaultWorkflow);

    const sampleWorkflows: WorkflowDefinition[] = [
      {
        id: 'sample_1',
        name: 'User Registration Workflow',
        description: 'Automated user registration with email verification',
        version: '1.0.0',
        nodes: [
          {
            id: 'trigger_1',
            type: 'trigger',
            data: { 
              label: 'User Registration',
              triggerType: 'webhook',
              status: 'active'
            },
            position: { x: 100, y: 100 },
            configuration: {}
          },
          {
            id: 'action_1',
            type: 'action',
            data: { 
              label: 'Send Welcome Email',
              connectorId: 'email-smtp'
            },
            position: { x: 100, y: 250 },
            configuration: {}
          }
        ],
        edges: [
          {
            id: 'edge_1',
            source: 'trigger_1',
            target: 'action_1'
          }
        ],
        triggers: [],
        variables: [],
        settings: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: userId,
        status: 'draft'
      },
      {
        id: 'sample_2',
        name: 'Order Processing Workflow',
        description: 'Process customer orders with approval and notifications',
        version: '1.0.0',
        nodes: [
          {
            id: 'trigger_2',
            type: 'trigger',
            data: { 
              label: 'New Order',
              triggerType: 'webhook',
              status: 'active'
            },
            position: { x: 100, y: 100 },
            configuration: {}
          },
          {
            id: 'condition_1',
            type: 'condition',
            data: { 
              label: 'Order > $1000?',
              condition: { field: 'amount', operator: 'greater_than', value: 1000 }
            },
            position: { x: 100, y: 250 },
            configuration: {}
          },
          {
            id: 'approval_1',
            type: 'approval',
            data: { 
              label: 'Manager Approval',
              status: 'idle'
            },
            position: { x: 50, y: 400 },
            configuration: {}
          },
          {
            id: 'action_2',
            type: 'action',
            data: { 
              label: 'Process Order',
              connectorId: 'database-sql'
            },
            position: { x: 150, y: 400 },
            configuration: {}
          }
        ],
        edges: [
          {
            id: 'edge_2',
            source: 'trigger_2',
            target: 'condition_1'
          },
          {
            id: 'edge_3',
            source: 'condition_1',
            target: 'approval_1',
            sourceHandle: 'true'
          },
          {
            id: 'edge_4',
            source: 'condition_1',
            target: 'action_2',
            sourceHandle: 'false'
          }
        ],
        triggers: [],
        variables: [],
        settings: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: userId,
        status: 'active'
      }
    ];

    setWorkflows(sampleWorkflows);
  }, [userId]);

  // Workflow engine event listeners
  useEffect(() => {
    const handleExecutionStarted = (execution: WorkflowExecution) => {
      setExecutions(prev => [execution, ...prev]);
      setIsExecuting(true);
      showNotification('Workflow execution started', 'info');
    };

    const handleExecutionCompleted = (execution: WorkflowExecution) => {
      setExecutions(prev => 
        prev.map(ex => ex.id === execution.id ? execution : ex)
      );
      setIsExecuting(false);
      showNotification('Workflow execution completed successfully', 'success');
    };

    const handleExecutionFailed = (execution: WorkflowExecution) => {
      setExecutions(prev => 
        prev.map(ex => ex.id === execution.id ? execution : ex)
      );
      setIsExecuting(false);
      showNotification(`Workflow execution failed: ${execution.error?.message}`, 'error');
    };

    workflowEngine.on('execution.started', handleExecutionStarted);
    workflowEngine.on('execution.completed', handleExecutionCompleted);
    workflowEngine.on('execution.failed', handleExecutionFailed);

    return () => {
      workflowEngine.off('execution.started', handleExecutionStarted);
      workflowEngine.off('execution.completed', handleExecutionCompleted);
      workflowEngine.off('execution.failed', handleExecutionFailed);
    };
  }, [workflowEngine]);

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleSaveWorkflow = useCallback((workflow: WorkflowDefinition) => {
    setWorkflows(prev => {
      const existing = prev.find(w => w.id === workflow.id);
      if (existing) {
        return prev.map(w => w.id === workflow.id ? workflow : w);
      } else {
        return [...prev, workflow];
      }
    });
    setCurrentWorkflow(workflow);
    showNotification('Workflow saved successfully', 'success');
  }, []);

  const handleRunWorkflow = useCallback(async (workflow: WorkflowDefinition) => {
    try {
      setIsExecuting(true);
      const inputData = { timestamp: new Date().toISOString(), userId };
      await workflowEngine.executeWorkflow(workflow, inputData, { userId });
    } catch (error) {
      setIsExecuting(false);
      showNotification(`Failed to run workflow: ${error.message}`, 'error');
    }
  }, [workflowEngine, userId]);

  const handleCreateNewWorkflow = () => {
    const newWorkflow: WorkflowDefinition = {
      id: `workflow_${Date.now()}`,
      name: 'New Workflow',
      description: 'AI-Generated Workflow',
      version: '1.0.0',
      nodes: [],
      edges: [],
      triggers: [],
      variables: [],
      settings: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: userId,
      status: 'draft'
    };

    setCurrentWorkflow(newWorkflow);
    setShowWorkflowList(false);
    setNewWorkflowDialogOpen(false);
  };

  const handleSelectWorkflow = (workflow: WorkflowDefinition) => {
    setCurrentWorkflow(workflow);
    setShowWorkflowList(false);
  };

  const handleBackToList = () => {
    setCurrentWorkflow(null);
    setShowWorkflowList(true);
  };

  const getWorkflowStats = () => {
    const totalWorkflows = workflows.length;
    const activeWorkflows = workflows.filter(w => w.status === 'active').length;
    const totalExecutions = executions.length;
    const successfulExecutions = executions.filter(e => e.status === 'completed').length;
    const successRate = totalExecutions > 0 ? Math.round((successfulExecutions / totalExecutions) * 100) : 0;

    return { totalWorkflows, activeWorkflows, totalExecutions, successRate };
  };

  const stats = getWorkflowStats();

  return (
    <Box sx={{ height: 'calc(100vh - 72px)', display: 'flex', flexDirection: 'column' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: '#6366f1' }}>
        <Toolbar>
          {onBackToFoundation && (
            <IconButton 
              color="inherit" 
              onClick={onBackToFoundation}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Flow Builder
          </Typography>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
            <Chip
              label={`${stats.totalWorkflows} Workflows`}
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            />
            <Chip
              label={`${stats.successRate}% Success Rate`}
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!showWorkflowList && (
              <Tooltip title="View All Workflows">
                <Button 
                  color="inherit" 
                  onClick={() => setShowWorkflowList(true)}
                  startIcon={<AnalyticsIcon />}
                >
                  My Workflows
                </Button>
              </Tooltip>
            )}

            <Tooltip title="Create New Workflow">
              <IconButton color="inherit" onClick={() => setNewWorkflowDialogOpen(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Execution History">
              <IconButton color="inherit" onClick={() => setExecutionHistoryOpen(true)}>
                <HistoryIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="More Options">
              <IconButton 
                color="inherit" 
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <MoreIcon />
              </IconButton>
            </Tooltip>

            {showWorkflowList && (
              <Button 
                color="inherit" 
                onClick={() => setShowWorkflowList(false)}
                sx={{ ml: 1 }}
              >
                Start Building
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {showWorkflowList ? (
          <WorkflowList 
            workflows={workflows}
            executions={executions}
            onSelectWorkflow={handleSelectWorkflow}
            onRunWorkflow={handleRunWorkflow}
            stats={stats}
          />
        ) : (
          <ChatWorkflowDesigner
            workflow={currentWorkflow || undefined}
            connectorManager={connectorManager}
            workflowEngine={workflowEngine}
            onSave={handleSaveWorkflow}
            onRun={handleRunWorkflow}
          />
        )}
      </Box>

      {/* More Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ExportIcon sx={{ mr: 1 }} />
          Export Workflows
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <ImportIcon sx={{ mr: 1 }} />
          Import Workflows
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <SettingsIcon sx={{ mr: 1 }} />
          Settings
        </MenuItem>
      </Menu>

      {/* New Workflow Dialog */}
      <Dialog 
        open={newWorkflowDialogOpen} 
        onClose={() => setNewWorkflowDialogOpen(false)}
      >
        <DialogTitle>Create New Workflow</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Start building a new automation workflow from scratch.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewWorkflowDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateNewWorkflow} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Execution History Dialog */}
      <ExecutionHistoryDialog
        open={executionHistoryOpen}
        executions={executions}
        onClose={() => setExecutionHistoryOpen(false)}
      />

      {/* Analytics Dialog */}
      <AnalyticsDialog
        open={analyticsOpen}
        workflows={workflows}
        executions={executions}
        onClose={() => setAnalyticsOpen(false)}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Workflow List Component
interface WorkflowListProps {
  workflows: WorkflowDefinition[];
  executions: WorkflowExecution[];
  onSelectWorkflow: (workflow: WorkflowDefinition) => void;
  onRunWorkflow: (workflow: WorkflowDefinition) => void;
  stats: any;
}

const WorkflowList: React.FC<WorkflowListProps> = ({
  workflows,
  executions,
  onSelectWorkflow,
  onRunWorkflow,
  stats
}) => {
  const getWorkflowExecutions = (workflowId: string) => {
    return executions.filter(e => e.workflowId === workflowId);
  };

  const getLastExecution = (workflowId: string) => {
    const workflowExecutions = getWorkflowExecutions(workflowId);
    return workflowExecutions.length > 0 ? workflowExecutions[0] : null;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Workflows
              </Typography>
              <Typography variant="h4">
                {stats.totalWorkflows}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Workflows
              </Typography>
              <Typography variant="h4">
                {stats.activeWorkflows}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Executions
              </Typography>
              <Typography variant="h4">
                {stats.totalExecutions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h4">
                {stats.successRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Workflows List */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your Workflows
        </Typography>
        <List>
          {workflows.map((workflow) => {
            const lastExecution = getLastExecution(workflow.id);
            const executionCount = getWorkflowExecutions(workflow.id).length;

            return (
              <ListItem
                key={workflow.id}
                button
                onClick={() => onSelectWorkflow(workflow)}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {workflow.name}
                      </Typography>
                      <Chip
                        label={workflow.status}
                        size="small"
                        color={workflow.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {workflow.description || 'No description'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {workflow.nodes.length} nodes • {executionCount} executions
                        {lastExecution && (
                          <> • Last run: {new Date(lastExecution.startTime).toLocaleString()}</>
                        )}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RunIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRunWorkflow(workflow);
                    }}
                  >
                    Run
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

// Execution History Dialog
interface ExecutionHistoryDialogProps {
  open: boolean;
  executions: WorkflowExecution[];
  onClose: () => void;
}

const ExecutionHistoryDialog: React.FC<ExecutionHistoryDialogProps> = ({
  open,
  executions,
  onClose
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Execution History</DialogTitle>
      <DialogContent>
        <List>
          {executions.map((execution) => (
            <ListItem key={execution.id}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">
                      {execution.workflowId}
                    </Typography>
                    <Chip
                      label={execution.status}
                      size="small"
                      color={
                        execution.status === 'completed' ? 'success' :
                        execution.status === 'failed' ? 'error' :
                        execution.status === 'running' ? 'primary' : 'default'
                      }
                    />
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2">
                      Started: {new Date(execution.startTime).toLocaleString()}
                    </Typography>
                    {execution.endTime && (
                      <Typography variant="body2">
                        Duration: {execution.metrics.duration}ms
                      </Typography>
                    )}
                    {execution.error && (
                      <Typography variant="body2" color="error">
                        Error: {execution.error.message}
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Analytics Dialog
interface AnalyticsDialogProps {
  open: boolean;
  workflows: WorkflowDefinition[];
  executions: WorkflowExecution[];
  onClose: () => void;
}

const AnalyticsDialog: React.FC<AnalyticsDialogProps> = ({
  open,
  workflows,
  executions,
  onClose
}) => {
  const getAnalytics = () => {
    const totalExecutions = executions.length;
    const successfulExecutions = executions.filter(e => e.status === 'completed').length;
    const failedExecutions = executions.filter(e => e.status === 'failed').length;
    const averageExecutionTime = executions.length > 0 
      ? Math.round(executions.reduce((sum, e) => sum + (e.metrics.duration || 0), 0) / executions.length)
      : 0;

    return {
      totalExecutions,
      successfulExecutions,
      failedExecutions,
      averageExecutionTime
    };
  };

  const analytics = getAnalytics();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Analytics</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Executions
                </Typography>
                <Typography variant="h4">
                  {analytics.totalExecutions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Successful Executions
                </Typography>
                <Typography variant="h4" color="success.main">
                  {analytics.successfulExecutions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed Executions
                </Typography>
                <Typography variant="h4" color="error.main">
                  {analytics.failedExecutions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg. Execution Time
                </Typography>
                <Typography variant="h4">
                  {analytics.averageExecutionTime}ms
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderApp;